import React from 'react';
import { Link } from 'react-router-dom';
import ContentCard from '../Layouts/ContentCard';
import LoadingSpiner from '../Components/Loader/LoadingSpiner';
import { FaExclamationCircle, FaStar } from 'react-icons/fa';
import { useListFavoritesQuery, useUnstarEntityMutation } from '../api/favorite';

interface FavoriteEntity {
    id: string;
    name: string;
    status?: string;
    project_id?: string;
}

interface SectionProps {
    title: string;
    entityType: 'Project' | 'Image';
    linkFor: (e: FavoriteEntity) => string;
}

function FavoriteSection({ title, entityType, linkFor }: SectionProps) {
    const { data, isLoading, isError } = useListFavoritesQuery(entityType);
    const [unstar, { isLoading: unstarLoading }] = useUnstarEntityMutation();

    if (isLoading) return <div className='text-center mt-4'><LoadingSpiner /></div>;
    if (isError) return <p className='mt-4'><FaExclamationCircle className='inline mr-2' /> Error loading {title.toLowerCase()}</p>;

    // server may return null holes for trashed/invalid favorites (app-apis#93)
    const items: FavoriteEntity[] = (data ?? []).filter(Boolean);

    return (
        <div className='mt-4'>
            <h3 className='font-semibold mb-2'>{title}</h3>
            {items.length === 0 ? (
                <p className='text-sm opacity-70'>No favorite {title.toLowerCase()} yet.</p>
            ) : (
                <table className='table w-full'>
                    <tbody>
                        {items.map((e) => (
                            <tr key={e.id}>
                                <td><Link to={linkFor(e)}>{e.name}</Link></td>
                                <td className='text-right'>
                                    <button
                                        className='btn btn-ghost btn-xs'
                                        disabled={unstarLoading}
                                        onClick={() => unstar({ entityId: e.id })}
                                    >
                                        <FaStar className='inline mr-1' /> Unstar
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default function FavoritePage() {
    return (
        <div className='container mx-auto'>
            <ContentCard title="Favorites">
                <FavoriteSection title="Projects" entityType="Project" linkFor={(e) => `/projects/${e.id}`} />
                <FavoriteSection title="Images" entityType="Image" linkFor={(e) => `/projects/${e.project_id}/images/${e.id}`} />
            </ContentCard>
        </div>
    );
}
